import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import ProductMovementHistoryFilters from './ProductMovementHistoryFilters';
import ProductMovementHistoryTable from './ProductMovementHistoryTable';
import ProductMovementHistoryDetailModal from './ProductMovementHistoryDetailModal';
import { useProductMovementHistory, useLocations, useUsers } from './ProductMovementHistory.hooks';
import { ProductHistoryFilterState, ProductHistorySort } from '../../../types/productHistory.types';
import CommonPagination from '../../common/Pagination/Pagination';
import { SelectChangeEvent } from '@mui/material';
import { ListPageMain, ListPageFiltersSection } from '../../common/list-page';
import { usePermission } from '../../../hooks/usePermission';
import { SETTINGS_PERMISSIONS } from '../../../constants/permissions.constants';

export interface ProductMovementHistoryProps {
  // Props can be added here if needed
}

const STORAGE_KEY = 'productHistoryFilters';

const ProductMovementHistory: React.FC<ProductMovementHistoryProps> = () => {
  // Load filters from sessionStorage on mount
  const loadFiltersFromStorage = useCallback((): ProductHistoryFilterState => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate that parsed object has expected structure
        if (parsed && typeof parsed === 'object') {
          return parsed as ProductHistoryFilterState;
        }
      }
    } catch (error) {
      console.error('Error loading filters from storage:', error);
    }
    return {};
  }, []);

  // Filter state - initialize from storage
  const [filters, setFilters] = useState<ProductHistoryFilterState>(loadFiltersFromStorage);

  // Pagination state
  const [pagination, setPagination] = useState<{ page: number; limit: number }>({
    page: 1,
    limit: 10,
  });

  // Sort state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sort, setSort] = useState<ProductHistorySort>({
    sortBy: 'performed_at',
    sortOrder: 'DESC',
  });

  // Detail modal state
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Fetch data
  const { data, meta, isLoading, error } = useProductMovementHistory(filters, pagination, sort);

  // Fetch locations and users for filters (users list requires settings.users VIEW)
  const canListUsers = usePermission(SETTINGS_PERMISSIONS.USERS.VIEW);
  const { locations } = useLocations();
  const { users } = useUsers(canListUsers);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      // Only save if there are active filters
      const hasActiveFilters = Object.values(filters).some(
        (value) => value !== undefined && value !== null && value !== '',
      );

      if (hasActiveFilters) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
      } else {
        // Clear storage if no active filters
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving filters to storage:', error);
    }
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: ProductHistoryFilterState) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((event: SelectChangeEvent<unknown>) => {
    const newLimit = Number(event.target.value);
    setPagination((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1, // Reset to first page
    }));
  }, []);

  // Handle view details
  const handleViewDetails = useCallback((id: number) => {
    setSelectedHistoryId(id);
    setIsDetailModalOpen(true);
  }, []);

  // Handle close detail modal
  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedHistoryId(null);
  }, []);

  // Pagination info
  const paginationInfo = useMemo(() => {
    return {
      currentPage: pagination.page,
      itemsPerPage: pagination.limit,
      totalItems: meta?.total || 0,
      totalPages: meta?.totalPages || 0,
    };
  }, [pagination, meta]);

  return (
    <ListPageMain sectionGap={1}>
      <ListPageFiltersSection>
        <ProductMovementHistoryFilters
          locations={Array.isArray(locations) ? locations : []}
          users={Array.isArray(users) ? users : []}
          canFilterByUser={canListUsers}
          onFilterChange={handleFilterChange}
          initialFilters={filters}
        />
      </ListPageFiltersSection>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <Box sx={{ py: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <>
          <ProductMovementHistoryTable data={data} onViewDetails={handleViewDetails} />

          {/* Pagination */}
          {data.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <CommonPagination
                currentPage={paginationInfo.currentPage}
                itemsPerPage={paginationInfo.itemsPerPage}
                totalItems={paginationInfo.totalItems}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 25, 50, 100]}
              />
            </Box>
          )}
        </>
      )}

      {/* Detail Modal */}
      <ProductMovementHistoryDetailModal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        historyId={selectedHistoryId}
      />
    </ListPageMain>
  );
};

export default ProductMovementHistory;
